import { omit, uniq, uniqBy } from 'lodash';

import { Action } from '../../actions';
import {
    ON_OPEN_MASS_ACTIONS_CALLOUTS,
    OnOpenMassActionsCalloutsAction
} from '../../actions/banners-edit/banners-edit-mass-actions';
import {
    ADD_CALLOUTS,
    ADD_CALLOUTS_SUCCESS,
    CALLOUTS_EDITOR_CHECKED_ALL_ITEMS,
    CALLOUTS_EDITOR_REMOVE_ALL_ITEMS_FROM_CHECKLIST,
    CALLOUTS_EDITOR_REMOVE_ITEM_FROM_CHECKLIST,
    HIDE_CALLOUTS_ERROR,
    HideCalloutsError,
    OPEN_CALLOUTS_EDITOR,
    OpenCalloutsEditor,
    REMOVE_CALLOUTS,
    REMOVE_CALLOUTS_SUCCESS,
    SET_CALLOUTS_EDITOR_ERRORS,
    SetCalloutsEditorErrors,
    SHOW_ADD_CALLOUTS_ERROR,
    ShowAddCalloutsError,
    TOGGLE_CALLOUTS_EDITOR_ITEM,
    ToggleCalloutsEditorItem,
    UPDATE_ADD_INPUT_CALLOUTS_EDITOR,
    UPDATE_CALLOUTS_DATA,
    UPDATE_CALLOUTS_LIST
} from '../../actions/banners-edit/callouts';
import {
    DELETE_BANNERS,
    RESET_BANNERS_EDIT,
    SAVE_BANNERS_SUCCESS,
    SaveBannersSuccess
} from '../../actions/bannersEdit';
import { selectMassActionsSelectedCallouts } from '../../selectors/bannerEdit/massActions/massActions';
import { calloutsTextErrorMapper } from '../../service/BannerEditErrors/ErrorsTextMapper/ErrorsTextMapper';
import {
    getDefaultMassActionsCalloutsState,
    isBannerEditMassActionsCalloutsAvailable
} from '../../service/BannersEditMassActionsHelpers/BannersEditMassActionsHelpers';
import { Callout, CalloutsEditorId } from '../../typings/banners';
import { CalloutsEditorData, CalloutsEditorState, EditedBanner, MassActionId } from '../../typings/bannersEditState';
import { EditorPlaceId, ValidationErrorData, ValidationResultItem } from '../../typings/common';
import { State } from '../../typings/state';

const showCalloutsAddError = (state: CalloutsEditorState, action: ShowAddCalloutsError): CalloutsEditorState => {
    const errors = uniqBy(
        [
            ...action.errors.map(
                (error: ValidationResultItem): ValidationErrorData => ({
                    code: error.code,
                    path: error.path,
                    text: calloutsTextErrorMapper(error)
                })
            ),
            ...(state.data[action.bannerId].errors || [])
        ],
        'text'
    );

    return {
        ...state,
        data: {
            ...state.data,
            [action.bannerId]: {
                ...state.data[action.bannerId],
                isAddingCallouts: false,
                isDeletingCallouts: false,
                errors: errors
            }
        }
    };
};

const hideCalloutsError = (state: CalloutsEditorState, action: HideCalloutsError): CalloutsEditorState => {
    const data = state.data[action.bannerId];
    const errors = (data.errors || []).filter((error: ValidationErrorData) => !action.paths.includes(error.path || ''));

    return {
        ...state,
        data: {
            ...state.data,
            [action.bannerId]: {
                ...data,
                errors: [...errors]
            }
        }
    };
};

const toggleCalloutsEditorItem = (
    state: CalloutsEditorState,
    action: ToggleCalloutsEditorItem
): CalloutsEditorState => {
    const { selected } = state.data[action.bannerId];

    return {
        ...state,
        data: {
            ...state.data,
            [action.bannerId]: {
                ...state.data[action.bannerId],
                selected: selected.includes(action.itemId)
                    ? selected.filter(id => id !== action.itemId)
                    : [...selected, action.itemId.toString()]
            }
        }
    };
};

const openCalloutsMassEditor = (
    state: CalloutsEditorState,
    action: OnOpenMassActionsCalloutsAction,
    fullState: State
): CalloutsEditorState => {
    return {
        ...state,
        data: {
            ...state.data,
            [action.massActionId]: {
                ...state.data[action.massActionId],
                selected: selectMassActionsSelectedCallouts(fullState),
                errors: undefined
            }
        }
    };
};

const openCalloutsEditor = (
    state: CalloutsEditorState,
    action: OpenCalloutsEditor,
    fullState: State
): CalloutsEditorState => {
    const banner: EditedBanner = fullState.editBanners.banners[action.bannerId];

    if (!isBannerEditMassActionsCalloutsAvailable(banner)) {
        return state;
    }

    const { selected } = banner.callouts;
    const { isAddingCallouts, isDeletingCallouts } = state.data[MassActionId.bannersEdit];

    return {
        ...state,
        data: {
            ...state.data,
            [action.bannerId]: {
                ...state.data[action.bannerId],
                isAddingCallouts,
                isDeletingCallouts,
                selected,
                text: ''
            }
        }
    };
};

const deleteCallouts = (state: CalloutsEditorState, ids: EditorPlaceId[]): CalloutsEditorState => {
    const newDataState = omit(state.data, ids);

    return {
        ...state,
        data: newDataState
    };
};

const setSavedCallouts = (state: CalloutsEditorState, action: SaveBannersSuccess): CalloutsEditorState => {
    const { added } = action.result;
    const calloutsData = state.data;

    if (added.banners.length) {
        added.banners.forEach((addedBanner, index) => {
            if (!addedBanner) {
                return;
            }
            const oldId = added.ids[index];
            const newId = String(addedBanner.id);

            calloutsData[newId] = {
                ...calloutsData[oldId]
            };

            delete calloutsData[oldId];
        });

        state = {
            ...state,
            data: calloutsData
        };
    }

    return state;
};

const setCalloutsEditorErrors = (state: CalloutsEditorState, action: SetCalloutsEditorErrors): CalloutsEditorState => {
    const callouts = state.data;
    const newCalloutsData = Object.keys(callouts).reduce(
        (acc: Record<CalloutsEditorId, CalloutsEditorData>, key: string) => {
            acc[key] = {
                ...callouts[key],
                errors: action.errors[key]
            };

            return acc;
        },
        {}
    );

    return {
        ...state,
        data: {
            ...state.data,
            ...newCalloutsData
        }
    };
};

export const calloutsEditorStateInitialState = {
    data: {
        ...getDefaultMassActionsCalloutsState()
    },
    list: []
};

export function calloutsReducer(
    state: CalloutsEditorState = calloutsEditorStateInitialState,
    action: Action,
    fullState: State
): CalloutsEditorState {
    switch (action.type) {
        case RESET_BANNERS_EDIT:
            return calloutsEditorStateInitialState;
        case ON_OPEN_MASS_ACTIONS_CALLOUTS:
            return openCalloutsMassEditor(state, action, fullState);
        case ADD_CALLOUTS:
            return {
                ...state,
                data: {
                    ...state.data,
                    [action.bannerId]: {
                        ...state.data[action.bannerId],
                        isAddingCallouts: true
                    }
                }
            };
        case REMOVE_CALLOUTS:
            return {
                ...state,
                data: {
                    ...state.data,
                    [action.bannerId]: {
                        ...state.data[action.bannerId],
                        isDeletingCallouts: true
                    }
                }
            };
        case SHOW_ADD_CALLOUTS_ERROR:
            return showCalloutsAddError(state, action);
        case HIDE_CALLOUTS_ERROR:
            return hideCalloutsError(state, action);
        case REMOVE_CALLOUTS_SUCCESS:
            return {
                ...state,
                list: state.list.filter(callout => !action.calloutIds.includes(callout.id)),
                data: {
                    ...state.data,
                    [action.bannerId]: {
                        ...state.data[action.bannerId],
                        selected: state.data[action.bannerId].selected.filter(
                            item => !action.calloutIds.includes(item)
                        ),
                        isDeletingCallouts: false
                    }
                }
            };
        case ADD_CALLOUTS_SUCCESS:
            return {
                ...state,
                list: [...state.list, ...action.newCallouts],
                data: {
                    ...state.data,
                    [action.bannerId]: {
                        ...state.data[action.bannerId],
                        isAddingCallouts: false,
                        text: '',
                        selected: [
                            ...(state.data[action.bannerId].selected || []),
                            ...action.newCallouts.map((callout: Callout) => callout.id.toString())
                        ]
                    }
                }
            };
        case UPDATE_ADD_INPUT_CALLOUTS_EDITOR:
            return {
                ...state,
                data: {
                    ...state.data,
                    [action.bannerId]: {
                        ...state.data[action.bannerId],
                        text: action.text
                    }
                }
            };
        case TOGGLE_CALLOUTS_EDITOR_ITEM:
            return toggleCalloutsEditorItem(state, action);
        case CALLOUTS_EDITOR_CHECKED_ALL_ITEMS:
            return {
                ...state,
                data: {
                    ...state.data,
                    [action.bannerId]: {
                        ...state.data[action.bannerId],
                        selected: action.isChecked
                            ? uniq([...state.data[action.bannerId].selected, ...action.selectedIds])
                            : []
                    }
                }
            };
        case CALLOUTS_EDITOR_REMOVE_ITEM_FROM_CHECKLIST:
            return {
                ...state,
                data: {
                    ...state.data,
                    [action.bannerId]: {
                        ...state.data[action.bannerId],
                        selected: state.data[action.bannerId].selected.filter(id => id !== action.itemId.toString())
                    }
                }
            };
        case CALLOUTS_EDITOR_REMOVE_ALL_ITEMS_FROM_CHECKLIST:
            return {
                ...state,
                data: {
                    ...state.data,
                    [action.bannerId]: {
                        ...state.data[action.bannerId],
                        selected: []
                    }
                }
            };
        case UPDATE_CALLOUTS_LIST:
            return {
                ...state,
                list: action.list
            };
        case UPDATE_CALLOUTS_DATA:
            return {
                ...state,
                data: {
                    ...state.data,
                    ...action.data
                }
            };
        case DELETE_BANNERS:
            return deleteCallouts(state, action.bannersIds);
        case OPEN_CALLOUTS_EDITOR:
            return openCalloutsEditor(state, action, fullState);
        case SAVE_BANNERS_SUCCESS:
            return setSavedCallouts(state, action);
        case SET_CALLOUTS_EDITOR_ERRORS:
            return setCalloutsEditorErrors(state, action);
        default:
            return state;
    }
}
